import { useCallback, useRef, useSyncExternalStore } from "react";
import { DEFAULT_PATCH } from "../model/defaults";
import { PARAMETER_BY_ID } from "../model/catalog";
import { LEGACY_SESSION_STORAGE_KEYS, normalizePatch, SESSION_STORAGE_KEY } from "../model/patch";
import type { CRTVarPatch, PadId, ParameterId, RuntimeState, WorkspaceTab } from "../model/types";

export interface AppState {
  patch: CRTVarPatch;
  runtime: RuntimeState;
  revision: number;
}

const emptyTelemetry = { rms: 0, peak: 0, low: 0, mid: 0, high: 0, envelope: 0, sequencerStep: -1, sampleTime: 0 };

function initialPatch(): CRTVarPatch {
  if (typeof window === "undefined") return structuredClone(DEFAULT_PATCH);
  try {
    const sourceKey = [SESSION_STORAGE_KEY, ...LEGACY_SESSION_STORAGE_KEYS].find((key) => localStorage.getItem(key) !== null);
    const raw = sourceKey ? localStorage.getItem(sourceKey) : null;
    const patch = raw ? normalizePatch(JSON.parse(raw)) : structuredClone(DEFAULT_PATCH);
    if (sourceKey && sourceKey !== SESSION_STORAGE_KEY) localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(patch));
    return patch;
  } catch {
    return structuredClone(DEFAULT_PATCH);
  }
}

let state: AppState = {
  patch: initialPatch(),
  runtime: {
    engineStatus: "locked",
    engineMessage: "Audio is waiting for your gesture",
    activeTab: "perform",
    bypass: false,
    safeOutput: true,
    recording: false,
    recordingSeconds: 0,
    sourceStatus: "ready",
    sourceMessage: "Internal generator",
    activePads: {},
    activeNotes: {},
    telemetry: emptyTelemetry,
    fps: 0,
    compositeLocked: true,
    patchDirty: false,
    midiStatus: typeof navigator !== "undefined" && navigator.requestMIDIAccess ? "off" : "unsupported",
  },
  revision: 0,
};

const listeners = new Set<() => void>();
let saveTimer: number | undefined;

function publish(next: AppState, persist = false) {
  state = { ...next, revision: state.revision + 1 };
  listeners.forEach((listener) => listener());
  if (persist && typeof window !== "undefined") {
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      try { localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state.patch)); } catch { /* Export remains available. */ }
    }, 250);
  }
}

export const appStore = {
  getSnapshot: () => state,
  subscribe(listener: () => void) { listeners.add(listener); return () => listeners.delete(listener); },
  replacePatch(patch: CRTVarPatch, dirty = false) {
    publish({ ...state, patch: normalizePatch(patch), runtime: { ...state.runtime, patchDirty: dirty } }, true);
  },
  updatePatch(updater: (patch: CRTVarPatch) => CRTVarPatch) {
    publish({ ...state, patch: updater(structuredClone(state.patch)), runtime: { ...state.runtime, patchDirty: true } }, true);
  },
  setParameter(id: ParameterId, value: number) {
    const descriptor = PARAMETER_BY_ID.get(id);
    if (!descriptor) return;
    const clamped = Math.min(descriptor.max, Math.max(descriptor.min, value));
    this.updatePatch((patch) => {
      const [section, key] = id.split(".") as ["video" | "synth", string];
      if (section === "video") (patch.video as unknown as Record<string, unknown>)[key] = clamped;
      else if (key.startsWith("osc1")) {
        const child = key.replace("osc1", "");
        const property = `${child.charAt(0).toLowerCase()}${child.slice(1)}`;
        (patch.synth.osc1 as unknown as Record<string, unknown>)[property] = clamped;
      } else if (key.startsWith("osc2")) {
        const child = key.replace("osc2", "");
        const property = `${child.charAt(0).toLowerCase()}${child.slice(1)}`;
        (patch.synth.osc2 as unknown as Record<string, unknown>)[property] = clamped;
      } else (patch.synth as unknown as Record<string, unknown>)[key] = clamped;
      return patch;
    });
  },
  setRuntime(update: Partial<RuntimeState>) { publish({ ...state, runtime: { ...state.runtime, ...update } }); },
  setTab(activeTab: WorkspaceTab) { this.setRuntime({ activeTab }); },
  setPad(pad: PadId, active: boolean) {
    this.setRuntime({ activePads: { ...state.runtime.activePads, [pad]: active } });
  },
  noteOn(owner: string, note: number) { this.setRuntime({ activeNotes: { ...state.runtime.activeNotes, [owner]: note } }); },
  noteOff(owner: string) {
    const activeNotes = { ...state.runtime.activeNotes };
    delete activeNotes[owner];
    this.setRuntime({ activeNotes });
  },
  panic() {
    this.setRuntime({ engineStatus: "panic", engineMessage: "Signal safe — resume explicitly", bypass: true, activePads: { panic: true }, activeNotes: {} });
  },
};

export function shallowEqual<T>(left: T, right: T): boolean {
  if (Object.is(left, right)) return true;
  if (!left || !right || typeof left !== "object" || typeof right !== "object") return false;
  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const keys = Object.keys(leftRecord);
  return keys.length === Object.keys(rightRecord).length && keys.every((key) => Object.is(leftRecord[key], rightRecord[key]));
}

/**
 * Selector-aware external-store subscription. The cached snapshot is important:
 * useSyncExternalStore requires referentially stable snapshots, while telemetry
 * deliberately publishes much more often than the React control surface needs.
 */
export function useAppStore<T>(selector: (value: AppState) => T, equal: (left: T, right: T) => boolean = Object.is): T {
  const selectorRef = useRef(selector);
  const equalRef = useRef(equal);
  const selectedRef = useRef<{ initialized: boolean; value?: T }>({ initialized: false });
  selectorRef.current = selector;
  equalRef.current = equal;

  const getSelectedSnapshot = useCallback(() => {
    const next = selectorRef.current(appStore.getSnapshot());
    if (selectedRef.current.initialized && equalRef.current(selectedRef.current.value as T, next)) {
      return selectedRef.current.value as T;
    }
    selectedRef.current = { initialized: true, value: next };
    return next;
  }, []);

  return useSyncExternalStore(appStore.subscribe, getSelectedSnapshot, getSelectedSnapshot);
}
